import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "./ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Slider } from "./ui/slider";
import {
  MapPin, Search, Check, ChevronDown, ChevronUp, Locate,
  Home, Camera, MessageSquare, DollarSign, Star, Info, Save,
} from "lucide-react";
import { toast } from "sonner";

import { PROPERTY } from "../constants/testIds";
import {
  PROPERTY_TYPES, ROOM_COUNTS, STATUS_LIST, USAGE_LIST, FURNISHING_LIST,
  ORIENTATION_LIST, HEATING_LIST, PARKING_LIST, DEED_LIST, CONDITION_LIST,
  FEATURES_POSITIVE, FEATURES_NEGATIVE, STATUS_TO_CATEGORY,
} from "../constants/propertyOptions";
import { saveDraft, loadDraft, clearDraft } from "../utils/autoDraft";
import { calculateQuickScore, verdictFromScore } from "../utils/quickScore";
import { CURRENCIES, formatMoney } from "../utils/currency";
import MoneyInput from "./MoneyInput";
import SingleChoiceSelect from "./SingleChoiceSelect";
import {
  FLOOR_OPTIONS,
  BUILDING_AGE_OPTIONS,
  mapStoredFloorToOption,
  mapStoredAgeToOption,
  ageOptionToPayload,
  floorOptionToPayload,
} from "../constants/floorAgeOptions";
import PhotoUploader from "./PhotoUploader";
import MeetingNotesTimeline from "./MeetingNotesTimeline";
import LocationPreview from "./LocationPreview";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  title: "", code: "", description: "",
  status: "Yeni Eklendi", category: "Gezeceklerim",
  property_type: "", room_count: "",
  country: "Türkiye", city: "", district: "", neighborhood: "",
  street: "", building_name: "", address: "",
  location: "", lat: null, lng: null,
  location_accuracy: null, location_captured_at: null,
  net_area: "", gross_area: "", current_floor: null,
  currency: "TRY", currency_other: "",
  initial_price: "", current_price: "", seller_last_price: "",
  my_offer: "", counter_offer: "", target_price: "",
  max_price: "", agreed_price: "",
  estimated_rent: "", aidat: "",
  renovation_cost: "", furniture_cost: "", extra_costs: "",
  usage: "", furnishing: "", orientation: "",
  heating: "", parking: "", deed_type: "", technical_condition: "",
  features: [],
  personal_like: null, location_score: null, rentability_score: null,
  negotiation_desire: "", personal_impression: "",
};

const PropertyFormDialog = ({ open, onClose, property, onSubmit }) => {
  const [tab, setTabRaw] = useState(() => {
    try {
      const t = new URLSearchParams(window.location.search).get("tab");
      return ["genel","konum","ozellikler","fiyat","notlar","fotograf"].includes(t) ? t : "genel";
    } catch { return "genel"; }
  });
  // Form-tab switch uses replaceState so the tab change never creates a new
  // Back-history entry.
  const setTab = useCallback((next) => {
    setTabRaw(next);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("tab", next);
      window.history.replaceState({ ...(window.history.state || {}), tab: next }, "", url.toString());
    } catch {}
  }, []);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [locationQuery, setLocationQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locked, setLocked] = useState(false);
  const [savedId, setSavedId] = useState(null); // for photos/notes once created
  const [savedProperty, setSavedProperty] = useState(null);
  // Bulunduğu Kat / Bina Yaşı — track selected option value separately from
  // the loaded raw value so legacy descriptive strings (e.g. "11-15 arası")
  // that don't map into the fixed option list are preserved verbatim.
  const [floorSelection, setFloorSelection] = useState("");
  const [ageSelection, setAgeSelection] = useState("");
  const draftKey = property?.id || "new";

  useEffect(() => {
    if (!open) return;
    if (property) {
      const merged = { ...emptyForm, ...property };
      merged.features = property.features || [];
      merged.category = property.category || STATUS_TO_CATEGORY[property.status] || "Gezeceklerim";
      setForm(merged);
      setLocationQuery(property.location || "");
      setLocked(!!property.lat);
      setSavedId(property.id);
      setSavedProperty(property);
      setFloorSelection(mapStoredFloorToOption(property.current_floor));
      setAgeSelection(mapStoredAgeToOption(property.building_age));
    } else {
      const draft = loadDraft("new");
      if (draft) {
        setForm({ ...emptyForm, ...draft });
        setLocationQuery(draft.location || "");
        setLocked(!!draft.lat);
        setFloorSelection(mapStoredFloorToOption(draft.current_floor));
        setAgeSelection(mapStoredAgeToOption(draft.building_age));
        toast.info("Kaydedilmiş taslak yüklendi");
      } else {
        setForm(emptyForm);
        setLocationQuery("");
        setLocked(false);
        setFloorSelection("");
        setAgeSelection("");
      }
      setSavedId(null);
      setSavedProperty(null);
    }
    setTab("genel");
    setSuggestions([]);
  }, [property, open]);

  // Auto-draft every ~1s
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => saveDraft(draftKey, form), 800);
    return () => clearTimeout(t);
  }, [form, open, draftKey]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggleFeature = (key) => {
    setForm((f) => {
      const has = (f.features || []).includes(key);
      return {
        ...f,
        features: has ? f.features.filter(x => x !== key) : [...(f.features || []), key],
      };
    });
  };

  const scoreResult = useMemo(() => calculateQuickScore(form), [form]);
  const verdict = verdictFromScore(scoreResult.score, scoreResult.completeness);
  // Kritik veri eşiği: en az 25% veri girilmediyse skoru gösterme
  const scoreReady = scoreResult.completeness >= 25;

  const searchLocation = async () => {
    if (!locationQuery || locationQuery.trim().length < 3) {
      toast.error("En az 3 karakter girin");
      return;
    }
    setSearching(true);
    setSuggestions([]);
    try {
      const { data } = await axios.get(`${API_URL}/api/geocode`, {
        params: { q: locationQuery }, withCredentials: true,
      });
      if (data.length === 0) toast.warning("Adres bulunamadı");
      setSuggestions(data);
    } catch (e) {
      toast.error("Adres arama başarısız");
    } finally { setSearching(false); }
  };

  const selectSuggestion = async (s) => {
    set({ location: s.short_name || s.display_name, lat: s.lat, lng: s.lng });
    setLocationQuery(s.short_name || s.display_name);
    setSuggestions([]);
    setLocked(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/reverse-geocode`, {
        params: { lat: s.lat, lng: s.lng }, withCredentials: true,
      });
      set({
        country: data.country || form.country,
        city: data.city || "",
        district: data.district || "",
        neighborhood: data.neighborhood || "",
        street: data.street || "",
      });
    } catch (e) { console.debug("form aux call failed (non-critical):", e?.message); }
    toast.success("Konum sabitlendi");
  };

  const captureLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Cihaz konum servisi desteklemiyor");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        const now = new Date().toISOString();
        set({
          lat: latitude, lng: longitude,
          location_accuracy: Math.round(accuracy),
          location_captured_at: now,
        });
        setLocked(true);
        // reverse geocode for address auto-fill
        try {
          const { data } = await axios.get(`${API_URL}/api/reverse-geocode`, {
            params: { lat: latitude, lng: longitude }, withCredentials: true,
          });
          set({
            location: data.display_name?.split(",").slice(0, 2).join(",") || form.location,
            country: data.country || form.country,
            city: data.city || form.city,
            district: data.district || form.district,
            neighborhood: data.neighborhood || form.neighborhood,
            street: data.street || form.street,
          });
        } catch (e) { console.debug("photo upload aux failed (non-critical):", e?.message); }
        toast.success(`Konum kaydedildi (±${Math.round(accuracy)}m)`);
        setLocating(false);
      },
      (err) => {
        setLocating(false);
        let msg = "Konum alınamadı";
        if (err.code === 1) msg = "Konum izni reddedildi. Aşağıdaki adres arama veya harita üzerinden manuel pin seçebilirsiniz.";
        else if (err.code === 2) msg = "Konum bilinmiyor. GPS sinyalinizi kontrol edin veya adres arama kullanın.";
        else if (err.code === 3) msg = "Konum alma zaman aşımına uğradı. Tekrar deneyin.";
        toast.error(msg, { duration: 6000 });
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!form.title || form.title.trim().length === 0) {
      toast.error("Kısa başlık zorunludur");
      setTab("genel");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      // Bina Yaşı — user picked an option: replace with canonical value.
      // No pick: preserve whatever was originally stored (legacy string or
      // pre-existing int).
      if (ageSelection !== "") {
        payload.building_age = ageOptionToPayload(ageSelection);
      }
      // Bulunduğu Kat — same rule.
      if (floorSelection !== "") {
        payload.current_floor = floorOptionToPayload(floorSelection);
      } else if (typeof payload.current_floor === "string") {
        const t = payload.current_floor.trim();
        payload.current_floor = t === "" ? null : t.slice(0, 50);
      }
      // Number conversions
      const numFields = ["net_area", "gross_area", "initial_price", "current_price",
        "seller_last_price", "my_offer", "counter_offer", "target_price", "max_price",
        "agreed_price", "estimated_rent", "aidat", "renovation_cost", "furniture_cost",
        "extra_costs", "lat", "lng"];
      numFields.forEach(k => {
        payload[k] = payload[k] === "" || payload[k] === null ? null : Number(payload[k]);
      });
      // Also update category from status
      if (payload.status && STATUS_TO_CATEGORY[payload.status]) {
        payload.category = STATUS_TO_CATEGORY[payload.status];
      }
      // Legacy fields for backward compat
      if (payload.current_price != null) payload.price = payload.current_price;
      if (payload.net_area != null) payload.area = payload.net_area;

      let response;
      if (property && property.id) {
        response = await axios.put(`${API_URL}/api/properties/${property.id}`, payload, {
          withCredentials: true,
        });
        toast.success("Gayrimenkul güncellendi");
      } else {
        response = await axios.post(`${API_URL}/api/properties`, payload, {
          withCredentials: true,
        });
        toast.success("Gayrimenkul eklendi");
        setSavedId(response.data.id);
        setSavedProperty(response.data);
      }
      clearDraft(draftKey);
      onSubmit?.(response.data);
      onClose?.();
    } catch (e) {
      console.error(e);
      toast.error("Kayıt başarısız");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="w-full h-full sm:max-w-3xl sm:!h-auto sm:!max-h-[calc(100dvh-24px)] bg-white dark:bg-[#18181B] border-[#E4E4E7] dark:border-[#27272A] overflow-hidden !flex !flex-col sm:!grid-cols-none sm:!gap-0 !p-0"
        style={{ maxHeight: "calc(100dvh - 24px)" }}
      >
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="text-xl sm:text-2xl font-bold text-[#0A0A0A] dark:text-[#FAFAFA]">
            {property ? "Gayrimenkul Düzenle" : "Yeni Gayrimenkul Ekle"}
          </DialogTitle>
          {/* Quick score summary */}
          <div className="mt-2 flex items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-2">
              {scoreReady ? (
                <>
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-white text-xs"
                    style={{ backgroundColor: verdict.color }}
                    data-testid="form-quick-score"
                  >
                    {scoreResult.score}
                  </div>
                  <div>
                    <div className="font-semibold text-[#0A0A0A] dark:text-[#FAFAFA]">
                      {verdict.label} {scoreResult.provisional && "(geçici)"}
                    </div>
                    <div className="text-[#52525B] dark:text-[#A1A1AA] text-[10px]">
                      Veri Yeterliliği %{scoreResult.completeness}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center border-2 border-dashed border-[#E4E4E7] dark:border-[#27272A] text-[#52525B] dark:text-[#A1A1AA]">
                    <Info className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-[#52525B] dark:text-[#A1A1AA]" data-testid="form-score-not-ready">
                      Henüz puanlanmadı
                    </div>
                    <div className="text-[#52525B] dark:text-[#A1A1AA] text-[10px]">
                      Veri Yeterliliği %{scoreResult.completeness} — puan için minimum bilgi eksik
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="text-[10px] text-[#52525B] dark:text-[#A1A1AA] italic">
              Taslak otomatik kaydediliyor
            </div>
          </div>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col overflow-hidden px-6">
          <TabsList className="grid grid-cols-6 mb-3 h-9">
            <TabsTrigger data-testid="tab-genel" value="genel" className="text-xs">
              <Home className="w-3 h-3 mr-1 hidden sm:inline" />Genel
            </TabsTrigger>
            <TabsTrigger data-testid="tab-konum" value="konum" className="text-xs">
              <MapPin className="w-3 h-3 mr-1 hidden sm:inline" />Konum
            </TabsTrigger>
            <TabsTrigger data-testid="tab-ozellikler" value="ozellikler" className="text-xs">
              <Info className="w-3 h-3 mr-1 hidden sm:inline" />Özellikler
            </TabsTrigger>
            <TabsTrigger data-testid="tab-fiyat" value="fiyat" className="text-xs">
              <DollarSign className="w-3 h-3 mr-1 hidden sm:inline" />Fiyat
            </TabsTrigger>
            <TabsTrigger data-testid="tab-medya" value="medya" className="text-xs">
              <Camera className="w-3 h-3 mr-1 hidden sm:inline" />Medya
            </TabsTrigger>
            <TabsTrigger data-testid="tab-puan" value="puan" className="text-xs">
              <Star className="w-3 h-3 mr-1 hidden sm:inline" />Puan
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto pb-4 pr-1">
            {/* GENEL */}
            <TabsContent value="genel" className="space-y-3 mt-0">
              <div>
                <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Kısa Başlık *</Label>
                <Input
                  data-testid={PROPERTY.titleInput}
                  value={form.title}
                  onChange={(e) => set({ title: e.target.value })}
                  placeholder="Örn: Moonlight Residence havuz manzaralı 1+1"
                  className="mt-1 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Gayrimenkul Kodu (opsiyonel)</Label>
                  <Input
                    data-testid="property-code-input"
                    value={form.code || ""}
                    onChange={(e) => set({ code: e.target.value })}
                    placeholder="H-01"
                    className="mt-1 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Durum</Label>
                  <Select value={form.status} onValueChange={(v) => set({ status: v })}>
                    <SelectTrigger data-testid="property-status-select" className="mt-1 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS_LIST.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <ChipRow label="Gayrimenkul" value={form.property_type} options={PROPERTY_TYPES} onChange={(v) => set({ property_type: v })} testId="chip-property-type" />
                <ChipRow label="Oda Sayısı" value={form.room_count} options={ROOM_COUNTS} onChange={(v) => set({ room_count: v })} testId="chip-room-count" />
              </div>
              <div>
                <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Açıklama (opsiyonel)</Label>
                <Textarea
                  data-testid={PROPERTY.descriptionInput}
                  rows={3}
                  value={form.description || ""}
                  onChange={(e) => set({ description: e.target.value })}
                  placeholder="Emlakçı/ev sahibi/müteahhitle konuştuğum genel bilgiler..."
                  className="mt-1 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]"
                />
              </div>

              {savedId && (
                <div className="pt-2">
                  <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA] flex items-center gap-1 mb-1">
                    <MessageSquare className="w-3 h-3" /> Görüşme Notları
                  </Label>
                  <MeetingNotesTimeline
                    propertyId={savedId}
                    notes={savedProperty?.meeting_notes || []}
                    currency={form.currency || "TRY"}
                    currencyOther={form.currency_other || ""}
                    onChange={async () => {
                      const { data } = await axios.get(`${API_URL}/api/properties`, { withCredentials: true });
                      const updated = data.find(p => p.id === savedId);
                      if (updated) setSavedProperty(updated);
                    }}
                  />
                </div>
              )}
            </TabsContent>

            {/* KONUM */}
            <TabsContent value="konum" className="space-y-3 mt-0">
              <div>
                <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA] flex items-center gap-2">
                  Adres Ara
                  {locked && <span className="text-[10px] text-emerald-600 dark:text-emerald-400 flex items-center gap-1"><Check className="w-3 h-3" />SABİTLENDİ</span>}
                </Label>
                <div className="mt-1 flex gap-2">
                  <div className="relative flex-1">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B] dark:text-[#A1A1AA] pointer-events-none" />
                    <Input
                      data-testid={PROPERTY.locationInput}
                      value={locationQuery}
                      onChange={(e) => { setLocationQuery(e.target.value); setLocked(false); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); searchLocation(); } }}
                      placeholder="Örn: Beşiktaş İstanbul, Berlin Kreuzberg"
                      className="pl-10 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]"
                    />
                  </div>
                  <Button type="button" data-testid="geocode-search-button" onClick={searchLocation} disabled={searching} className="bg-[#0A0A0A] hover:bg-[#18181B] text-white dark:bg-[#FAFAFA] dark:text-[#0A0A0A]">
                    <Search className={`w-4 h-4 mr-2 ${searching ? "animate-spin" : ""}`} />{searching ? "..." : "Ara"}
                  </Button>
                </div>
                {suggestions.length > 0 && (
                  <div data-testid="geocode-suggestions" className="mt-2 border border-[#E4E4E7] dark:border-[#27272A] rounded-lg overflow-hidden divide-y divide-[#E4E4E7] dark:divide-[#27272A] bg-white dark:bg-[#09090B]">
                    {suggestions.map((s, i) => (
                      <button key={i} type="button" data-testid={`geocode-suggestion-${i}`} onClick={() => selectSuggestion(s)}
                        className="w-full text-left px-4 py-3 hover:bg-[#F9FAFB] dark:hover:bg-[#18181B] flex items-start gap-3">
                        <MapPin className="w-4 h-4 text-[#F59E0B] mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-[#0A0A0A] dark:text-[#FAFAFA] truncate">{s.short_name}</div>
                          <div className="text-xs text-[#52525B] dark:text-[#A1A1AA] truncate">{s.display_name}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <Button
                type="button"
                data-testid="capture-location-button"
                onClick={captureLocation}
                disabled={locating}
                variant="outline"
                className="w-full border-[#F59E0B] text-[#F59E0B] hover:bg-[#FEF3C7] dark:hover:bg-[#F59E0B]/10"
              >
                <Locate className={`w-4 h-4 mr-2 ${locating ? "animate-pulse" : ""}`} />
                {locating ? "Konum alınıyor..." : "Konumumu Kaydet (GPS)"}
              </Button>

              {form.lat && form.lng && (
                <LocationPreview
                  lat={Number(form.lat)}
                  lng={Number(form.lng)}
                  accuracy={form.location_accuracy}
                  capturedAt={form.location_captured_at}
                  onCorrect={(newLat, newLng) => set({
                    lat: newLat,
                    lng: newLng,
                    location_accuracy: null, // manual override — accuracy no longer meaningful
                    location_captured_at: new Date().toISOString(),
                  })}
                />
              )}

              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Ülke</Label>
                  <Input value={form.country || ""} onChange={(e) => set({ country: e.target.value })} className="mt-1 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]" /></div>
                <div><Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Şehir</Label>
                  <Input value={form.city || ""} onChange={(e) => set({ city: e.target.value })} className="mt-1 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]" /></div>
                <div><Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">İlçe</Label>
                  <Input value={form.district || ""} onChange={(e) => set({ district: e.target.value })} className="mt-1 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]" /></div>
                <div><Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Mahalle</Label>
                  <Input value={form.neighborhood || ""} onChange={(e) => set({ neighborhood: e.target.value })} className="mt-1 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]" /></div>
                <div><Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Sokak</Label>
                  <Input value={form.street || ""} onChange={(e) => set({ street: e.target.value })} className="mt-1 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]" /></div>
                <div><Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Site/Bina</Label>
                  <Input value={form.building_name || ""} onChange={(e) => set({ building_name: e.target.value })} className="mt-1 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]" /></div>
              </div>
              <div>
                <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Açık Adres</Label>
                <Textarea rows={2} value={form.address || ""} onChange={(e) => set({ address: e.target.value })} className="mt-1 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]" />
              </div>
            </TabsContent>

            {/* ÖZELLİKLER */}
            <TabsContent value="ozellikler" className="space-y-4 mt-0">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Net m²</Label>
                  <Input type="number" value={form.net_area || ""} onChange={(e) => set({ net_area: e.target.value })} className="mt-1 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]" /></div>
                <div><Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Brüt m²</Label>
                  <Input type="number" value={form.gross_area || ""} onChange={(e) => set({ gross_area: e.target.value })} className="mt-1 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]" /></div>
              </div>

              {/* Bulunduğu Kat — single-choice select. Legacy descriptive
                  values that don't map into the option list are shown as
                  a hint and preserved unchanged on save. */}
              <div>
                <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">
                  Bulunduğu Kat
                </Label>
                <SingleChoiceSelect
                  testId="current-floor-select"
                  value={floorSelection}
                  onChange={setFloorSelection}
                  options={FLOOR_OPTIONS}
                  placeholder="Kat seçin"
                  legacyText={
                    floorSelection === "" &&
                    form.current_floor != null &&
                    String(form.current_floor).trim() !== ""
                      ? String(form.current_floor)
                      : null
                  }
                />
              </div>

              {/* Bina Yaşı — single-choice select 0..50 + "Yapım aşamasında".
                  Legacy descriptive/out-of-range values shown as hint and
                  preserved unchanged. */}
              <div>
                <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">
                  Bina Yaşı
                </Label>
                <SingleChoiceSelect
                  testId="building-age-select"
                  value={ageSelection}
                  onChange={setAgeSelection}
                  options={BUILDING_AGE_OPTIONS}
                  placeholder="Bina yaşı seçin"
                  legacyText={
                    ageSelection === "" &&
                    form.building_age != null &&
                    String(form.building_age).trim() !== ""
                      ? String(form.building_age)
                      : null
                  }
                />
              </div>

              {/* Room count, technical condition, features, etc. */}

              <ChipRow label="Kullanım Durumu" value={form.usage} options={USAGE_LIST} onChange={(v) => set({ usage: v })} testId="chip-usage" />
              <ChipRow label="Eşya" value={form.furnishing} options={FURNISHING_LIST} onChange={(v) => set({ furnishing: v })} testId="chip-furnishing" />
              <ChipRow label="Cephe" value={form.orientation} options={ORIENTATION_LIST} onChange={(v) => set({ orientation: v })} testId="chip-orientation" />
              <ChipRow label="Isıtma" value={form.heating} options={HEATING_LIST} onChange={(v) => set({ heating: v })} testId="chip-heating" />
              <ChipRow label="Otopark" value={form.parking} options={PARKING_LIST} onChange={(v) => set({ parking: v })} testId="chip-parking" />
              <ChipRow label="Tapu" value={form.deed_type} options={DEED_LIST} onChange={(v) => set({ deed_type: v })} testId="chip-deed" />
              <ChipRow label="Teknik Durum" value={form.technical_condition} options={CONDITION_LIST} onChange={(v) => set({ technical_condition: v })} testId="chip-condition" />

              <FeatureToggles
                label="Olumlu Özellikler"
                options={FEATURES_POSITIVE}
                selected={form.features || []}
                onToggle={toggleFeature}
                variant="positive"
              />
              <FeatureToggles
                label="Olumsuz Gözlemler"
                options={FEATURES_NEGATIVE}
                selected={form.features || []}
                onToggle={toggleFeature}
                variant="negative"
              />
            </TabsContent>

            {/* FİYAT */}
            <TabsContent value="fiyat" className="space-y-3 mt-0">
              {/* Currency selector */}
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Para Birimi</Label>
                  <Select value={form.currency || "TRY"} onValueChange={(v) => set({ currency: v })}>
                    <SelectTrigger data-testid="currency-select" className="mt-1 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map(c => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {form.currency === "Diğer" && (
                  <div className="flex-1">
                    <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Kısaltma / Sembol</Label>
                    <Input
                      data-testid="currency-other-input"
                      value={form.currency_other || ""}
                      onChange={(e) => set({ currency_other: e.target.value.slice(0, 8) })}
                      placeholder="Örn: CHF, AED"
                      className="mt-1 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]"
                    />
                  </div>
                )}
                <div className="text-[10px] text-[#52525B] dark:text-[#A1A1AA] pb-2 whitespace-nowrap">
                  Fiyatlar orijinal para biriminde saklanır
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <PriceField label="İlk İlan Fiyatı" value={form.initial_price} onChange={(v) => set({ initial_price: v })} currency={form.currency} />
                <PriceField label="Güncel İlan Fiyatı" value={form.current_price} onChange={(v) => set({ current_price: v })} currency={form.currency} />
                <PriceField label="Satıcının Son Fiyatı" value={form.seller_last_price} onChange={(v) => set({ seller_last_price: v })} currency={form.currency} />
                <PriceField label="Teklifim" value={form.my_offer} onChange={(v) => set({ my_offer: v })} currency={form.currency} />
                <PriceField label="Karşı Teklif" value={form.counter_offer} onChange={(v) => set({ counter_offer: v })} currency={form.currency} />
                <PriceField label="Hedef Satın Alma" value={form.target_price} onChange={(v) => set({ target_price: v })} currency={form.currency} />
                <PriceField label="Maks. Satın Alma" value={form.max_price} onChange={(v) => set({ max_price: v })} currency={form.currency} />
                <PriceField label="Anlaşılan Fiyat" value={form.agreed_price} onChange={(v) => set({ agreed_price: v })} currency={form.currency} />
                <PriceField label="Tahmini Aylık Kira" value={form.estimated_rent} onChange={(v) => set({ estimated_rent: v })} currency={form.currency} />
                <PriceField label="Aidat" value={form.aidat} onChange={(v) => set({ aidat: v })} currency={form.currency} />
                <PriceField label="Tadilat Masrafı" value={form.renovation_cost} onChange={(v) => set({ renovation_cost: v })} currency={form.currency} />
                <PriceField label="Eşya Masrafı" value={form.furniture_cost} onChange={(v) => set({ furniture_cost: v })} currency={form.currency} />
                <PriceField label="Diğer Ek Masraf" value={form.extra_costs} onChange={(v) => set({ extra_costs: v })} currency={form.currency} />
              </div>

              {/* Auto calculations */}
              <PriceAutoCalc form={form} />
            </TabsContent>

            {/* MEDYA */}
            <TabsContent value="medya" className="mt-0">
              {savedId ? (
                <PhotoUploader
                  propertyId={savedId}
                  photos={savedProperty?.photos || []}
                  onChange={async (payload) => {
                    // refresh property to get updated photos
                    const { data } = await axios.get(`${API_URL}/api/properties`, { withCredentials: true });
                    const updated = data.find(p => p.id === savedId);
                    if (updated) setSavedProperty(updated);
                  }}
                />
              ) : (
                <div className="text-center py-8 text-sm text-[#52525B] dark:text-[#A1A1AA]">
                  <Camera className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Fotoğraf ekleyebilmek için önce ilanı kaydedin.</p>
                  <p className="text-xs mt-1">Sadece <b>Kısa Başlık</b> yeterli, gerisi sonra tamamlanabilir.</p>
                </div>
              )}
            </TabsContent>

            {/* PUAN */}
            <TabsContent value="puan" className="space-y-4 mt-0">
              {scoreReady ? (
                <div className="p-4 rounded-lg border-2" style={{ borderColor: verdict.color + "50", backgroundColor: verdict.color + "0D" }}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center text-2xl font-black text-white" style={{ backgroundColor: verdict.color }}>{scoreResult.score}</div>
                    <div className="flex-1">
                      <div className="text-lg font-bold text-[#0A0A0A] dark:text-[#FAFAFA]">
                        {verdict.label}
                        {scoreResult.provisional && <span className="ml-2 text-xs text-orange-600">geçici — veri eksik</span>}
                      </div>
                      <div className="text-xs text-[#52525B] dark:text-[#A1A1AA]">
                        Yapısal Yatırım Puanı
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-[#E4E4E7] dark:border-[#27272A] grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest font-bold text-[#52525B] dark:text-[#A1A1AA]">AI Puanı (Hızlı)</div>
                      <div className="text-2xl font-bold text-[#0A0A0A] dark:text-[#FAFAFA]">{scoreResult.score}<span className="text-sm text-[#52525B] dark:text-[#A1A1AA]">/100</span></div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest font-bold text-[#52525B] dark:text-[#A1A1AA]">Veri Yeterliliği</div>
                      <div className="text-2xl font-bold text-[#F59E0B]">%{scoreResult.completeness}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs mt-3">
                    <div>
                      <div className="font-bold text-emerald-600 dark:text-emerald-400 uppercase text-[10px] tracking-widest">Olumlu</div>
                      <ul className="mt-1 space-y-0.5">
                        {scoreResult.positives.length === 0 && <li className="text-[#52525B] dark:text-[#A1A1AA]">—</li>}
                        {scoreResult.positives.map((p, i) => <li key={i} className="text-[#0A0A0A] dark:text-[#FAFAFA]">✓ {p}</li>)}
                      </ul>
                    </div>
                    <div>
                      <div className="font-bold text-red-600 dark:text-red-400 uppercase text-[10px] tracking-widest">Olumsuz</div>
                      <ul className="mt-1 space-y-0.5">
                        {scoreResult.negatives.length === 0 && <li className="text-[#52525B] dark:text-[#A1A1AA]">—</li>}
                        {scoreResult.negatives.map((p, i) => <li key={i} className="text-[#0A0A0A] dark:text-[#FAFAFA]">✗ {p}</li>)}
                      </ul>
                    </div>
                  </div>
                  {scoreResult.missing.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-[#E4E4E7] dark:border-[#27272A]">
                      <div className="font-bold text-[#52525B] dark:text-[#A1A1AA] uppercase text-[10px] tracking-widest">Eksik Kritik Bilgi</div>
                      <div className="mt-1 text-xs text-[#0A0A0A] dark:text-[#FAFAFA]">{scoreResult.missing.join(" • ")}</div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-6 rounded-lg border-2 border-dashed border-[#E4E4E7] dark:border-[#27272A] text-center">
                  <Info className="w-8 h-8 mx-auto mb-3 text-[#52525B] dark:text-[#A1A1AA] opacity-60" />
                  <div className="font-bold text-[#0A0A0A] dark:text-[#FAFAFA]">Henüz puanlanmadı</div>
                  <div className="text-xs text-[#52525B] dark:text-[#A1A1AA] mt-1">
                    Yeterli veri girildikten sonra geçici AI puanı otomatik hesaplanır.
                  </div>
                  <div className="mt-3 inline-flex items-center gap-2 text-xs bg-[#F9FAFB] dark:bg-[#09090B] px-3 py-2 rounded border border-[#E4E4E7] dark:border-[#27272A]">
                    <span className="font-semibold text-[#F59E0B]">Veri Yeterliliği: %{scoreResult.completeness}</span>
                    <span className="text-[#52525B] dark:text-[#A1A1AA]">(en az %25 gerekli)</span>
                  </div>
                  {scoreResult.missing.length > 0 && (
                    <div className="mt-3 text-[11px] text-[#52525B] dark:text-[#A1A1AA]">
                      <span className="font-bold uppercase tracking-widest">Öneri: </span>{scoreResult.missing.slice(0, 4).join(", ")}
                    </div>
                  )}
                </div>
              )}

              <div className="pt-2 border-t border-[#E4E4E7] dark:border-[#27272A]">
                <div className="text-xs font-bold uppercase tracking-widest text-[#52525B] dark:text-[#A1A1AA] mb-3">
                  Kişisel Puanım (1–10)
                </div>
                <ScoreSlider label="Kişisel Beğeni" value={form.personal_like} onChange={(v) => set({ personal_like: v })} />
                <ScoreSlider label="Konum Puanım" value={form.location_score} onChange={(v) => set({ location_score: v })} />
                <ScoreSlider label="Kiralanabilirlik" value={form.rentability_score} onChange={(v) => set({ rentability_score: v })} />
                <div className="mt-3">
                  <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Pazarlık İsteğim</Label>
                  <Select value={form.negotiation_desire || ""} onValueChange={(v) => set({ negotiation_desire: v })}>
                    <SelectTrigger className="mt-1 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]"><SelectValue placeholder="Seçin" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="düşük">Düşük</SelectItem>
                      <SelectItem value="orta">Orta</SelectItem>
                      <SelectItem value="yüksek">Yüksek</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="mt-3">
                  <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Genel Kişisel İzlenim</Label>
                  <Textarea rows={2} value={form.personal_impression || ""} onChange={(e) => set({ personal_impression: e.target.value })} className="mt-1 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]" />
                </div>
              </div>
            </TabsContent>
          </div>
        </Tabs>

        <div className="border-t border-[#E4E4E7] dark:border-[#27272A] p-4 flex gap-2 bg-white dark:bg-[#18181B] flex-shrink-0" style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
          <Button type="button" variant="outline" onClick={onClose} className="flex-1 sm:flex-none border-[#E4E4E7] dark:border-[#27272A]">
            İptal
          </Button>
          <Button
            type="button"
            data-testid={PROPERTY.submitButton}
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 bg-[#F59E0B] hover:bg-[#D97706] text-white"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? "Kaydediliyor..." : property ? "Güncelle" : "Kaydet"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// --- Reusable subcomponents ---

const ChipRow = ({ label, value, options, onChange, testId }) => (
  <div>
    <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">{label}</Label>
    <div className="mt-1 flex flex-wrap gap-1.5">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          data-testid={`${testId}-${opt.toLowerCase().replace(/\s+/g, "-")}`}
          onClick={() => onChange(value === opt ? "" : opt)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors duration-150 ${
            value === opt
              ? "bg-[#0A0A0A] text-white border-[#0A0A0A] dark:bg-[#FAFAFA] dark:text-[#0A0A0A] dark:border-[#FAFAFA]"
              : "bg-white dark:bg-[#09090B] text-[#52525B] dark:text-[#A1A1AA] border-[#E4E4E7] dark:border-[#27272A] hover:border-[#F59E0B]"
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  </div>
);

const FeatureToggles = ({ label, options, selected, onToggle, variant }) => (
  <div>
    <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">{label}</Label>
    <div className="mt-1 flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const active = selected.includes(opt.key);
        const bg = active
          ? variant === "positive"
            ? "bg-emerald-500 text-white border-emerald-500"
            : "bg-red-500 text-white border-red-500"
          : "bg-white dark:bg-[#09090B] text-[#52525B] dark:text-[#A1A1AA] border-[#E4E4E7] dark:border-[#27272A]";
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => onToggle(opt.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors duration-150 ${bg}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  </div>
);

const PriceField = ({ label, value, onChange, currency = "TRY", currencyOther = "", testId }) => {
  return (
    <div>
      <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">{label}</Label>
      <div className="mt-1">
        <MoneyInput
          value={value}
          onChange={(n) => onChange(n == null ? "" : n)}
          currency={currency}
          currencyOther={currencyOther}
          data-testid={testId}
        />
      </div>
    </div>
  );
};

const ScoreSlider = ({ label, value, onChange }) => (
  <div className="mb-3">
    <div className="flex items-center justify-between mb-1">
      <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">{label}</Label>
      <span className="text-xs font-bold text-[#F59E0B]">{value != null ? `${value}/10` : "—"}</span>
    </div>
    <Slider
      value={[value ?? 0]}
      min={0}
      max={10}
      step={1}
      onValueChange={(v) => onChange(v[0])}
    />
  </div>
);

const PRICE_BASE_ORDER = [
  { key: "agreed_price",     label: "Anlaşılan Fiyat" },
  { key: "target_price",     label: "Hedef Satın Alma" },
  { key: "counter_offer",    label: "Karşı Teklif" },
  { key: "my_offer",         label: "Teklifim" },
  { key: "seller_last_price",label: "Satıcının Son Fiyatı" },
  { key: "current_price",    label: "Güncel İlan Fiyatı" },
  { key: "initial_price",    label: "İlk İlan Fiyatı" },
];

const PriceAutoCalc = ({ form }) => {
  const cur = form.currency || "TRY";
  const other = form.currency_other || "";
  const num = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const numOr0 = (v) => num(v) ?? 0;

  // A) Base price with clear priority + which field was used
  let baseKey = null, baseLabel = null, basePrice = null;
  for (const opt of PRICE_BASE_ORDER) {
    const v = num(form[opt.key]);
    if (v != null && v > 0) { baseKey = opt.key; baseLabel = opt.label; basePrice = v; break; }
  }

  // B) Total additional costs
  const reno = numOr0(form.renovation_cost);
  const furn = numOr0(form.furniture_cost);
  const extra = numOr0(form.extra_costs);
  const totalExtra = reno + furn + extra;

  // C) Estimated total investment
  const totalInvest = basePrice != null ? basePrice + totalExtra : null;

  // D) Rent-based
  const estRent = num(form.estimated_rent);
  const yearlyGrossRent = estRent != null ? estRent * 12 : null;
  const grossYield = (yearlyGrossRent != null && totalInvest != null && totalInvest > 0)
    ? (yearlyGrossRent / totalInvest) * 100 : null;
  const paybackYears = (yearlyGrossRent != null && yearlyGrossRent > 0 && totalInvest != null)
    ? totalInvest / yearlyGrossRent : null;

  // E) Area calc
  const netArea = num(form.net_area);
  const grossArea = num(form.gross_area);
  const perM2Net = (totalInvest != null && netArea && netArea > 0) ? totalInvest / netArea : null;
  const perM2Gross = (totalInvest != null && grossArea && grossArea > 0 && !netArea)
    ? totalInvest / grossArea : null;
  const aidat = num(form.aidat);

  const fmt = (v) => formatMoney(v, cur, other);
  const pct = (v) => v == null ? null : `%${v.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const years = (v) => v == null ? null : `${v.toLocaleString("tr-TR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} yıl`;

  const Row = ({ label, value, testId, muted = false }) => (
    <div className="flex justify-between items-baseline text-xs py-1.5 border-b border-[#E4E4E7] dark:border-[#27272A] last:border-0">
      <span className={muted ? "text-[#A1A1AA] dark:text-[#71717A]" : "text-[#52525B] dark:text-[#A1A1AA]"}>{label}</span>
      <span data-testid={testId} className={muted ? "text-[#A1A1AA] dark:text-[#71717A]" : "font-semibold text-[#0A0A0A] dark:text-[#FAFAFA]"}>{value}</span>
    </div>
  );

  const hints = [];
  if (basePrice == null) hints.push("Hesaplama için en az bir satın alma fiyatı girin (Teklifim / Hedef Satın Alma / …).");
  if (basePrice != null && estRent == null) hints.push("Kira getirisi için Tahmini Aylık Kira girin.");
  if (basePrice != null && !netArea && !grossArea) hints.push("m² fiyatı için Net m² (veya Brüt m²) girin.");

  return (
    <div
      data-testid="price-auto-calc"
      className="mt-4 p-4 bg-[#F9FAFB] dark:bg-[#09090B] rounded-lg border border-[#E4E4E7] dark:border-[#27272A]"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-bold uppercase tracking-widest text-[#52525B] dark:text-[#A1A1AA]">
          Otomatik Hesaplama ({cur === "Diğer" ? (other || "Diğer") : cur})
        </div>
        {basePrice != null && (
          <div className="text-[10px] text-[#52525B] dark:text-[#A1A1AA]">
            Baz alınan fiyat: <b className="text-[#0A0A0A] dark:text-[#FAFAFA]">{baseLabel}</b>
          </div>
        )}
      </div>

      {basePrice == null ? (
        <div className="text-xs text-[#52525B] dark:text-[#A1A1AA] italic py-2">
          {hints[0] || "Veri bekleniyor…"}
        </div>
      ) : (
        <>
          <Row label={`Baz alınan (${baseLabel})`} value={fmt(basePrice)} testId="calc-base-price" />
          <Row label="Toplam ek masraf" value={fmt(totalExtra)} testId="calc-total-extra" muted={totalExtra === 0} />
          <Row label="Tahmini toplam yatırım" value={fmt(totalInvest)} testId="calc-total-invest" />
          {aidat != null && aidat > 0 && (
            <Row label="Aylık aidat (bilgi)" value={fmt(aidat)} testId="calc-aidat" muted />
          )}
          {estRent != null && estRent > 0 && (
            <>
              <Row label="Tahmini aylık kira" value={fmt(estRent)} testId="calc-monthly-rent" />
              <Row label="Yıllık brüt kira" value={fmt(yearlyGrossRent)} testId="calc-yearly-rent" />
              <Row label="Brüt yıllık kira getirisi" value={pct(grossYield)} testId="calc-gross-yield" />
              <Row label="Brüt amortisman süresi" value={paybackYears != null && paybackYears < 200 ? years(paybackYears) : "—"} testId="calc-payback" />
            </>
          )}
          {perM2Net != null && (
            <Row label="Yatırım / Net m²" value={`${fmt(Math.round(perM2Net))}/m²`} testId="calc-per-m2-net" />
          )}
          {perM2Gross != null && (
            <Row label="Yatırım / Brüt m² (net yok)" value={`${fmt(Math.round(perM2Gross))}/m²`} testId="calc-per-m2-gross" muted />
          )}
        </>
      )}

      {hints.length > 0 && basePrice != null && (
        <div className="mt-2 text-[10px] text-[#52525B] dark:text-[#A1A1AA] italic">
          {hints.map((h, i) => <div key={i}>• {h}</div>)}
        </div>
      )}
    </div>
  );
};

export default PropertyFormDialog;
